import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const CONSENT_VERSION = 'm365-family-2026-10-11-v2';
const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"], #checkout, [data-ol-lc-kasse], a[href*="/checkout"], a[href*="/pages/kasse"], shopify-accelerated-checkout-cart';

/**
 * @typedef {{key: string, variant_id: number, quantity: number, properties: Record<string,string> | null}} InviteLine
 * @extends {Component<{dialog:HTMLDialogElement,checks:HTMLInputElement[],confirm:HTMLButtonElement,close:HTMLButtonElement,status:HTMLElement,cartState:HTMLScriptElement}>}
 */
class InviteConsent extends Component {
  requiredRefs = ['dialog', 'checks', 'confirm', 'close', 'status', 'cartState'];
  #abort;
  #mode = 'preview';
  #form = null;
  #returnFocus = null;
  #busy = false;
  #items = [];
  #authorizedForm = null;
  #cartRequest = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    try { this.#items = JSON.parse(this.refs.cartState.textContent || '{}').items || []; } catch { this.#items = []; }
    window.addEventListener('click', this.#onClick, { capture: true, signal });
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    this.addEventListener('change', this.#updateButton, { signal });
    this.refs.close.addEventListener('click', () => this.refs.dialog.close(), { signal });
    this.refs.confirm.addEventListener('click', this.#confirm, { signal });
    this.refs.dialog.addEventListener('close', this.#closed, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartChanged, { signal });
    document.addEventListener('shopify:section:load', this.#enableTriggers, { signal });
    this.#enableTriggers();
  }

  updatedCallback() { super.updatedCallback(); this.#updateButton(); }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#cartRequest++;
    if (this.refs.dialog?.open) this.refs.dialog.close();
    this.#form = null;
    super.disconnectedCallback();
  }

  #enableTriggers = () => {
    for (const button of document.querySelectorAll('[data-ol24-invite-trigger]')) {
      if (button instanceof HTMLButtonElement && button.dataset.available === 'true') button.disabled = false;
    }
  };

  #inviteLines() { return this.#items.filter(item => String(item.variant_id) === this.dataset.variantId); }
  #hasConsent(item) {
    return item.properties?._ol24_invite_consent === CONSENT_VERSION && [1, 2, 3, 4].every(n => item.properties?.[`_ol24_invite_ack_${n}`] === 'yes');
  }

  #onClick = event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-ol24-invite-trigger], [data-ol24-invite-preview]');
    if (trigger instanceof HTMLElement && !trigger.hasAttribute('disabled')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const form = trigger.dataset.formId ? document.getElementById(trigger.dataset.formId) : null;
      this.#open(form instanceof HTMLFormElement ? 'product' : 'preview', form, trigger);
      return;
    }
    if (event.target.closest(CHECKOUT_SELECTOR)) this.#guardCheckout(event);
  };

  #onSubmit = event => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    if (form.closest('[data-ol24-invite-form]')) {
      if (this.#authorizedForm === form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#open('product', form, event.submitter || document.activeElement);
      return;
    }
    if (event.submitter?.matches(CHECKOUT_SELECTOR)) this.#guardCheckout(event);
  };

  #guardCheckout(event) {
    const lines = this.#inviteLines();
    if (!lines.some(item => !this.#hasConsent(item) || item.quantity !== 1) && lines.length <= 1) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#open('cart', null, event.target);
    if (lines.length > 1 || lines.some(item => item.quantity !== 1)) {
      this.refs.status.textContent = this.dataset.quantityText;
      this.refs.confirm.disabled = true;
    }
  }

  #open(mode, form, trigger) {
    if (this.refs.dialog.open || this.#busy) return;
    this.#mode = mode;
    this.#form = form;
    this.#returnFocus = trigger instanceof HTMLElement ? trigger : null;
    for (const checkbox of this.refs.checks) checkbox.checked = false;
    this.refs.status.textContent = '';
    this.refs.confirm.textContent = mode === 'preview' ? this.dataset.readText : mode === 'cart' ? this.dataset.cartText : this.dataset.addText;
    this.#updateButton();
    this.refs.dialog.showModal();
    this.refs.checks[0]?.focus();
  }

  #updateButton = () => {
    const lines = this.#inviteLines();
    const invalidQuantity = this.#mode === 'cart' && (lines.length > 1 || lines.some(item => item.quantity !== 1));
    this.refs.confirm.disabled = this.#busy || invalidQuantity || this.refs.checks.length !== 4 || !this.refs.checks.every(input => input.checked);
  };

  #properties(existing = {}) {
    return { ...existing, _ol24_invite_consent: CONSENT_VERSION, _ol24_invite_ack_1: 'yes', _ol24_invite_ack_2: 'yes', _ol24_invite_ack_3: 'yes', _ol24_invite_ack_4: 'yes', _ol24_invite_duration: '12m-from-link', 'Microsoft 365': this.dataset.lineLabel };
  }

  #readCart = async () => {
    const request = ++this.#cartRequest;
    const response = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: this.#abort.signal });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    if (request === this.#cartRequest) this.#items = cart.items || [];
    return cart;
  };

  #cartChanged = async event => {
    try {
      if (event.promise) await event.promise;
      await this.#readCart();
    } catch (error) { if (error.name !== 'AbortError' && this.refs.dialog.open) this.refs.status.textContent = this.dataset.errorText; }
  };

  #confirm = async () => {
    if (this.#busy || this.refs.checks.length !== 4 || !this.refs.checks.every(input => input.checked)) {
      this.refs.status.textContent = this.dataset.checksText;
      return;
    }
    if (this.#mode === 'preview') { this.refs.dialog.close(); return; }
    this.#busy = true;
    this.#updateButton();
    this.refs.status.textContent = '';
    try {
      await this.#readCart();
      const lines = this.#inviteLines();
      if (this.#mode === 'cart') {
        if (lines.length > 1 || lines.some(item => item.quantity !== 1)) throw new Error('quantity');
        for (const item of lines) {
          if (this.#hasConsent(item)) continue;
          const response = await fetch(`${Theme.routes.cart_url}/change.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal: this.#abort.signal, body: JSON.stringify({ id: item.key, quantity: 1, properties: this.#properties(item.properties || {}) }) });
          if (!response.ok) throw new Error('Cart update failed');
        }
        window.location.assign(Theme.routes.cart_url);
        return;
      }
      if (lines.length) throw new Error('existing');
      const form = this.#form;
      if (!(form instanceof HTMLFormElement) || !form.isConnected) throw new Error('Form unavailable');
      const id = form.querySelector('input[name="id"]');
      if (!(id instanceof HTMLInputElement) || id.value !== this.dataset.variantId) throw new Error('Variant changed');
      const quantity = form.querySelector('input[name="quantity"]');
      if (quantity instanceof HTMLInputElement) quantity.value = '1';
      for (const [key, value] of Object.entries(this.#properties())) {
        const inputName = `properties[${key}]`;
        let input = Array.from(form.elements).find(element => element instanceof HTMLInputElement && element.name === inputName);
        if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = inputName; form.append(input); }
        input.value = value;
      }
      id.disabled = false;
      this.#authorizedForm = form;
      this.refs.dialog.close();
      form.requestSubmit();
      this.#authorizedForm = null;
      // The theme synchronously snapshots FormData before its asynchronous cart request.
      id.disabled = true;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.refs.status.textContent = error.message === 'quantity' ? this.dataset.quantityText : error.message === 'existing' ? this.dataset.existingText : this.dataset.errorText;
    } finally {
      this.#authorizedForm = null;
      this.#busy = false;
      this.#updateButton();
    }
  };

  #closed = () => {
    this.#form = null;
    for (const input of this.refs.checks) input.checked = false;
    this.refs.confirm.disabled = true;
    if (this.#returnFocus?.isConnected) this.#returnFocus.focus();
    this.#returnFocus = null;
  };
}

if (!customElements.get('ol24-invite-consent')) customElements.define('ol24-invite-consent', InviteConsent);
